import React, { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pagination from "react-bootstrap/Pagination";
import { ListGroup } from "react-bootstrap";
import { faFilePdf, faFileWord, faFileExcel, faFileImage, faFile, faFileAlt } from '@fortawesome/free-regular-svg-icons';
import { fetchLibraryDataPerPage, IFilesResponse } from "../../api/api";
import { IInnerProject } from "../../types/inner_page";

interface ProjectFilesWidgetProps {
  language: string;
  pageItem: IInnerProject;
  
}

const fileIconMap: { [key: string]: any } = {
  pdf: faFilePdf,
  doc: faFileWord,
  docx: faFileWord,
  xls: faFileExcel,
  xlsx: faFileExcel,
  png: faFileImage,
  jpg: faFileImage,
  jpeg: faFileImage,
  txt: faFileAlt,
};

const getIconForExtension = (extension: string) => {
  return fileIconMap[extension.toLowerCase()] || faFile;
};

const ITEMS_PER_PAGE = 10;

const ProjectFilesWidget: React.FC<ProjectFilesWidgetProps> = ({ pageItem }) => {
  const { i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { isLoading, error, data } = useQuery<IFilesResponse>(
    ["libraryData", i18n.language, currentPage],
    () => fetchLibraryDataPerPage({ language: i18n.language, page: currentPage, pageSize: ITEMS_PER_PAGE }),
    {
      keepPreviousData: true,
      onSuccess: (responseData) => {
        setTotalPages(responseData.meta.pagination.pageCount);
      },
    }
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data...</div>;
  if (!data || !data.data.length) return <div>No data available</div>;

  return (
    <div>
      <ListGroup variant="flush" className="custom-list-group">
        {data.data.map((item) => {
          const file = item.attributes.file?.data;
          if (!file) return null; // If file data is missing, skip rendering this item

          return (
            <ListGroup.Item key={file.id} className="custom-list-item">
              <a
                href={`${process.env.REACT_APP_CMS}${file.attributes.url}`}
                target="_blank"
                rel="noopener noreferrer"
                download={file.attributes.name}
                className="file-link"
              >
                <FontAwesomeIcon icon={getIconForExtension(file.attributes.ext)} className="file-icon me-2 p-2" />
                <span className="file-name">{item.attributes.Title}</span>
              </a>
            </ListGroup.Item>
          );
        })}
      </ListGroup>

      {/* Enhanced Pagination Controls */}
      {totalPages > 1 && (
        <Pagination className="mt-3 en_font">
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          />
          {[...Array(totalPages)].map((_, pageIndex) => (
            <Pagination.Item
              key={pageIndex + 1}
              active={pageIndex + 1 === currentPage}
              onClick={() => setCurrentPage(pageIndex + 1)}
            >
              {pageIndex + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      )}
    </div>
  );
};

export default ProjectFilesWidget;
